import type { ReactNode } from 'react'

import { cn } from '@gto/ui/cn'
import { Typography, type TypographyColor, type TypographyVariant } from '@gto/ui/typography'

import { HighlightedText } from '../highlighted-text'
import { InlineHighlightedText } from '../inline-highlighted-text'
import type { ModalBlockAlign, ModalParagraphStyle } from './modal-block-types'

/**
 * The three designed looks. The highlight is always one step brighter than
 * the copy around it: the primary text colour inside secondary copy, and the
 * accent inside copy that is already primary.
 */
const PARAGRAPH_STYLE = {
  body: { variant: 'body1', color: 'text.secondary', highlightClassName: 'text-text-primary' },
  emphasis: {
    variant: 'subtitle1',
    color: 'text.primary',
    highlightClassName: 'text-primary-main',
  },
  lead: { variant: 'h6', color: 'text.primary', highlightClassName: 'text-primary-main' },
} as const satisfies Record<
  ModalParagraphStyle,
  { variant: TypographyVariant; color: TypographyColor; highlightClassName: string }
>

type ModalParagraphBlockProps = {
  text: ReactNode
  highlight?: string
  centerOnMobile?: boolean
  align?: ModalBlockAlign
  style?: ModalParagraphStyle
}

/**
 * An authored highlight phrase keeps the paragraph's own look, as it always
 * has. Without one, the copy reads the `**bold**` and `==primary==` markers
 * every other announcement surface reads. The two never mix: a phrase is
 * found by plain substring, and a marker inside it would split the match.
 */
const renderParagraphText = (
  text: ReactNode,
  highlight: string | undefined,
  highlightClassName: string,
): ReactNode => {
  if (typeof text !== 'string') {
    return text
  }

  if (highlight !== undefined && highlight !== '') {
    return (
      <HighlightedText
        highlight={highlight}
        highlightClassName={highlightClassName}
        text={text}
      />
    )
  }

  return <InlineHighlightedText text={text} />
}

export const ModalParagraphBlock = ({
  text,
  highlight,
  align,
  style = 'body',
  centerOnMobile,
}: ModalParagraphBlockProps) => {
  const look = PARAGRAPH_STYLE[style]

  return (
    <Typography
      color={look.color}
      // Always a paragraph element: `subtitle1` and `h6` would otherwise render
      // as headings and join the dialog's outline.
      component="p"
      variant={look.variant}
      className={cn(
        'w-full whitespace-pre-line [overflow-wrap:anywhere]',
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        centerOnMobile === true && 'sm:text-center',
      )}
    >
      {renderParagraphText(text, highlight, look.highlightClassName)}
    </Typography>
  )
}
